import {
  prefersReducedMotion,
  subscribeMotionPreference,
} from '@/utils/journeyMotion'

const REVEAL_SELECTORS = [
  '.home-intelligent-video__copy',
  '.home-intelligent-video__media-frame',
  '.live-ocean__inner > h2',
  '.live-ocean__feature',
  '.operational-proof__statement',
  '.operational-proof__partners',
  '.product-systems__inner > h2',
  '.home-applications__inner > h2',
  '.brand-value__visual',
  '.brand-value__content',
  '.trust-partners__audiences > h2',
  '.cta-content',
  '.platform-story .feature-grid',
  '.system-family .industry-content > h2',
  '.product-benefits .product-content > h2',
  '.angler-live-camera__introduction',
  '.angler-live-camera__frame',
  '.product-specifications__heading',
  '.technical-ledger',
  '.operational-profile__layout > h2',
  '.deployment-scene',
  '.deployment-live > h2',
  '.deployment-live .video-container',
  '.case-study > section:not(.deployment-live) > h2',
  '.periscope-ai-intro__layout',
  '.periscope-ai-case-study__story',
  '.periscope-ai-outputs__heading',
  '.periscope-ai-outputs__list',
  '.periscope-ai-principles__layout',
  '.periscope-ai-applications__layout',
  '.periscope-ai-cta__content',
  '.mission-section .about-content',
  '.people-section .about-content > h2',
  '.team-title',
  '.consult-section .about-content',
  '.resources-section .resources-content > h2',
  '.faq-grid',
  '.funding-section .resource-grid',
  '.project-support-section .resource-grid',
  '.documents-section .resources-content',
  '.contact-intro',
  '.contact-form-panel',
  '.compare-content > .section-title',
  '.comparison-table-wrapper',
  '.summary-grid',
  '.legal-header',
  '.legal-page > section',
  '.footer-mark',
]

const STAGGER_GROUPS = [
  ['.product-systems__layout', ':scope > .system-object'],
  ['.home-applications__layout', '.home-application'],
  ['.trust-partners__ledger', ':scope > div'],
  ['.industry-grid', ':scope > .industry-card'],
  ['.systems-grid', ':scope > .system-card'],
  ['.benefit-grid', ':scope > .benefit-card'],
  ['.ideal-grid', ':scope > .card'],
  ['.periscope-ai-workflow__list', ':scope > .periscope-ai-workflow__step'],
  ['.periscope-ai-metrics', ':scope > .periscope-ai-metric'],
  ['.periscope-ai-outputs__list', ':scope > article'],
  ['.leadership-grid', ':scope > .person-card'],
  ['.team-grid', ':scope > .person-card'],
  ['.footer-grid', ':scope > section'],
]

const HERO_SEQUENCES = [
  ['.home-hero', '.home-hero__primary > h1, .home-hero__primary > p, .home-hero__actions, .home-hero__secondary'],
  ['.industry-hero', '.industry-content > h1, .industry-content > p'],
  ['.product-hero', '.hero-text > h1, .hero-text > h2, .hero-text > p, .hero-text > .button, .hero-image'],
  ['.periscope-ai-hero', '.periscope-ai-hero__copy, .periscope-ai-hero__media'],
  ['.case-study', ':scope > h1, :scope > .meta, :scope > .meta + p, :scope > .meta + p + p'],
  ['.about-hero', '.about-content > h1, .about-content > h2, .about-content > p'],
  ['.resources-hero', '.resources-content > h1, .resources-content > p'],
  ['.contact-page', '.contact-intro, .contact-form-panel'],
  ['.compare-hero', '.compare-content > h1, .compare-content > p'],
]

const PREPARED_CLASSES = [
  'motion-reveal',
  'motion-stagger',
  'motion-stagger-item',
  'motion-hero-item',
  'is-motion-visible',
]

function queryAll(root, selector) {
  try {
    return Array.from(root.querySelectorAll(selector))
  } catch (error) {
    return []
  }
}

function isInitiallyVisible(element) {
  const bounds = element.getBoundingClientRect()
  return bounds.bottom > 0 && bounds.top < window.innerHeight
}

export function createSiteMotion(root) {
  let observer = null
  let reduced = prefersReducedMotion()
  let destroyed = false
  const observed = new Set()

  const reveal = (element) => {
    element.classList.add('is-motion-visible')
    if (observer) observer.unobserve(element)
    observed.delete(element)
  }

  const clearObserver = () => {
    if (observer) observer.disconnect()
    observer = null
    observed.clear()
  }

  const clearPreparedState = () => {
    queryAll(root, PREPARED_CLASSES.map((name) => `.${name}`).join(',')).forEach(
      (element) => {
        PREPARED_CLASSES.forEach((name) => element.classList.remove(name))
        element.style.removeProperty('--motion-order')
      },
    )
  }

  const handleEntries = (entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting || entry.intersectionRatio > 0) reveal(entry.target)
    })
  }

  const observe = (element) => {
    if (isInitiallyVisible(element)) {
      element.classList.add('is-motion-visible')
      return
    }

    observed.add(element)
    observer.observe(element)
  }

  const prepareHeroSequences = () => {
    HERO_SEQUENCES.forEach(([containerSelector, itemSelector]) => {
      queryAll(root, containerSelector).forEach((container) => {
        queryAll(container, itemSelector).forEach((item, index) => {
          item.classList.add('motion-hero-item')
          item.style.setProperty('--motion-order', Math.min(index, 4))
        })
      })
    })
  }

  const prepareReveals = () => {
    queryAll(root, REVEAL_SELECTORS.join(',')).forEach((element) => {
      element.classList.add('motion-reveal')
      observe(element)
    })

    STAGGER_GROUPS.forEach(([containerSelector, itemSelector]) => {
      queryAll(root, containerSelector).forEach((container) => {
        const items = queryAll(container, itemSelector)
        if (!items.length) return

        container.classList.add('motion-stagger')
        items.forEach((item, index) => {
          item.classList.add('motion-stagger-item')
          item.style.setProperty('--motion-order', Math.min(index, 4))
        })
        observe(container)
      })
    })
  }

  const preparePageEntry = () => {
    const page = root.querySelector('#main-content > *')
    if (page) page.classList.add('motion-page-enter')
  }

  const refresh = () => {
    if (destroyed) return

    clearObserver()
    root.classList.remove('motion-enabled')
    clearPreparedState()

    const previousPage = root.querySelector('.motion-page-enter')
    if (previousPage) previousPage.classList.remove('motion-page-enter')

    if (reduced || typeof IntersectionObserver === 'undefined') return

    try {
      observer = new IntersectionObserver(handleEntries, {
        rootMargin: '0px 0px -8% 0px',
        threshold: [0, 0.12],
      })
      prepareHeroSequences()
      prepareReveals()
      preparePageEntry()
      root.classList.add('motion-enabled')
    } catch (error) {
      clearObserver()
      clearPreparedState()
      root.classList.remove('motion-enabled')
    }
  }

  const handleFocus = (event) => {
    const revealTarget = event.target.closest('.motion-reveal')
    if (revealTarget) reveal(revealTarget)

    const staggerTarget = event.target.closest('.motion-stagger')
    if (staggerTarget) reveal(staggerTarget)
  }

  root.addEventListener('focusin', handleFocus)
  const stopWatchingPreference = subscribeMotionPreference((shouldReduce) => {
    if (reduced === shouldReduce) return
    reduced = shouldReduce
    refresh()
  })

  return {
    refresh,
    destroy() {
      destroyed = true
      clearObserver()
      stopWatchingPreference()
      root.removeEventListener('focusin', handleFocus)
      clearPreparedState()
      root.classList.remove('motion-enabled')
    },
  }
}
